import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import PreviewCotizacion from '@/components/cd2b/PreviewCotizacion'
import PageContainer from '@/components/common/PageContainer'
import Stepper from '@/components/common/Stepper'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCotizacion } from './editor/useCotizacion'
import { resumenAnual } from '@/lib/cicloPago'
import { PASOS } from './editor/paquete'
import PasoCliente from './editor/PasoCliente'
import PasoPaquete from './editor/PasoPaquete'
import PasoCobro from './editor/PasoCobro'
import PasoRevisar from './editor/PasoRevisar'
import ResumenFijo from './editor/ResumenFijo'
import VistaSoloLectura from './editor/VistaSoloLectura'

/**
 * Editor de cotizaciones cd-2b como asistente de cuatro pasos (docs/179, fase 2):
 * Cliente y país → Paquete → Cobro y servicios → Revisar y enviar.
 *
 * Este archivo solo ORQUESTA: el estado y las llamadas viven en `useCotizacion`, y cada paso en
 * `editor/`. El paso va en la URL (`?paso=2`) para que recargar no devuelva al asesor al principio;
 * sin `paso`, una cotización que ya salió (enviada, aprobada, pagada…) abre en «Revisar y enviar»,
 * que es donde está lo que se hace después de vender.
 *
 * Los cuatro pasos se montan SIEMPRE y se ocultan los que no tocan: varios bloques (identidad
 * fiscal, catálogo del contador) guardan estado propio que se perdería al ir y volver.
 */
export default function ConsultorCotizacionEditor() {
  const navigate = useNavigate()
  const { id } = useParams()
  const c = useCotizacion(id)
  const [busqueda, setBusqueda] = useSearchParams()

  const pasoUrl = Number(busqueda.get('paso'))
  // Una PAGADA solo se edita para completar los datos de la entrega, y esos viven en el paso 1.
  const pasoPorDefecto = !c.esNueva && c.propuesta && !['borrador', 'pagada'].includes(c.propuesta.estado) ? 4 : 1
  const paso = PASOS.some((p) => p.n === pasoUrl) ? pasoUrl : pasoPorDefecto

  // En una cotización nueva solo se puede saltar a los pasos ya vistos; una guardada, a todos.
  const [maxVisto, setMaxVisto] = useState(paso)
  useEffect(() => { setMaxVisto((m) => Math.max(m, paso)) }, [paso])
  // `?seccion=identidad-fiscal` (desde «Entregar a producción» en la ficha): lleva la vista al
  // bloque que falta completar en cuanto la cotización termina de cargar.
  const seccionUrl = busqueda.get('seccion')
  useEffect(() => {
    if (!seccionUrl || c.loading) return
    const t = setTimeout(() => document.getElementById(seccionUrl)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    return () => clearTimeout(t)
  }, [seccionUrl, c.loading])

  const irAPaso = (n) => {
    const b = new URLSearchParams(busqueda)
    b.set('paso', String(n))
    setBusqueda(b, { replace: true })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const alGuardar = async () => {
    const guardadaId = await c.guardar()
    // Recién creada: se abre por su id en «Revisar y enviar», donde ya se puede crear la demo.
    if (guardadaId && c.esNueva) navigate(`/consultor/cotizaciones/${guardadaId}?paso=4`)
  }

  if (c.loading) {
    return <PageContainer><p className="text-body-sm text-neutral-500 dark:text-neutral-400 py-8 text-center">Cargando…</p></PageContainer>
  }

  if (c.soloLectura) {
    return <VistaSoloLectura propuesta={c.propuesta} edicionLabel={c.edicionLabel} />
  }

  const pasoActual = PASOS[paso - 1]
  const habilitados = PASOS.filter((p) => !c.esNueva || p.n <= maxVisto).map((p) => p.clave)
  const muestraGuardar = !c.esNueva || paso === 4

  return (
    <PageContainer>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/consultor/cotizaciones')} aria-label="Volver">
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0">
          <h2 className="font-display text-h3 sm:text-h2 font-semibold text-foreground">
            {c.esNueva ? 'Nueva cotización cd-2b' : 'Editar cotización cd-2b'}
          </h2>
          {!c.esNueva && c.form.empresaNombre && (
            <p className="text-body-sm text-subtle truncate">{c.form.empresaNombre}</p>
          )}
        </div>
      </div>

      <Stepper
        pasos={PASOS}
        actual={pasoActual.clave}
        onIr={(clave) => irAPaso(PASOS.find((p) => p.clave === clave).n)}
        habilitados={habilitados}
      />

      {/* Pagada: el asistente solo sirve para completar lo que pide la entrega. El paquete que
          muestra es el de la VENTA original; el vigente (con sus addendas) vive en la ficha. */}
      {c.esPagada && (
        <Alert variant="info">
          <AlertDescription>
            Esta cotización ya está <span className="font-medium">pagada</span>: aquí solo se completan los
            datos para entregarla a producción (país, giro e identidad fiscal y, si el cliente trae el suyo, el
            catálogo de cuentas de su contador en el paso 4). El paquete de abajo es el de la
            venta original; el paquete vigente, con sus ampliaciones, está en la ficha del cliente.
            {c.propuesta?.subscriptionId && (
              <>
                {' '}
                <Link to={`/clientes/${c.propuesta.subscriptionId}/paquete`} className="font-medium underline">
                  Ver paquete vigente
                </Link>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {c.propuesta?.estado === 'rechazada' && (
        <Alert variant="warning">
          <AlertDescription>
            El cliente pidió cambios
            {c.propuesta.rechazo?.motivo ? <>: “{c.propuesta.rechazo.motivo}”</> : '.'}
            {' '}Al guardar, la cotización vuelve a borrador para que se la reenvíes.
          </AlertDescription>
        </Alert>
      )}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-6">
        <div className="min-w-0 space-y-6">
          <section hidden={paso !== 1} aria-label={PASOS[0].titulo}><PasoCliente c={c} /></section>
          <section hidden={paso !== 2} aria-label={PASOS[1].titulo}><PasoPaquete c={c} /></section>
          <section hidden={paso !== 3} aria-label={PASOS[2].titulo}><PasoCobro c={c} /></section>
          <section hidden={paso !== 4} aria-label={PASOS[3].titulo}><PasoRevisar c={c} onIrAPaso={irAPaso} /></section>

          {/* En el teléfono los botones van apilados a todo lo ancho y la acción principal queda
              ABAJO, a mano del pulgar; desde sm, una sola fila como siempre. */}
          <div className="flex flex-col gap-3 border-t border-line pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
              {paso > 1 && (
                <Button variant="outline" onClick={() => irAPaso(paso - 1)}>
                  <ArrowLeft size={16} className="mr-1.5" /> Atrás
                </Button>
              )}
              <Button variant="ghost" className="sm:hidden" onClick={() => navigate('/consultor/cotizaciones')}>Cancelar</Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              {/*
                Un botón deshabilitado sin motivo es una trampa: se pulsa, no pasa nada, y no hay forma
                de saber qué falta. El motivo va AL LADO, no en un aviso más arriba que ya se pasó.
              */}
              {muestraGuardar && c.frenoDeCreacion && (
                <span className="text-body-sm text-warning-700 dark:text-warning-300">{c.frenoDeCreacion}</span>
              )}
              <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate('/consultor/cotizaciones')}>Cancelar</Button>
              {muestraGuardar && (
                <Button variant={paso < 4 ? 'outline' : 'default'} className="w-full sm:w-auto" onClick={alGuardar} disabled={c.saving || Boolean(c.frenoDeCreacion)}>
                  {c.saving ? 'Guardando…' : (c.esNueva ? 'Crear borrador' : 'Guardar cambios')}
                </Button>
              )}
              {paso < 4 && (
                <Button className="w-full sm:w-auto" onClick={() => irAPaso(paso + 1)}>
                  Siguiente <ArrowRight size={16} className="ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <ResumenFijo c={c} />
      </div>

      <PreviewCotizacion open={c.previewOpen} onClose={() => c.setPreviewOpen(false)} {...c.previewData} anual={resumenAnual(c.form.ciclo, c.comision?.anual)} />
      {c.dialogo}
    </PageContainer>
  )
}
